import { getDate, isEarlierDate, isSameDate, isValidDate, stringToDate } from '@shared-logic/utils';
import { Order, Quotas } from './order.interface';
import { Exchange, OrderExchange } from './order-exchange.interface';
import { orderStatusConst, orderTags } from './order.constants';
import { SYNGENTA_CODE_BUYER } from '@shared-logic/subjects';
import { getIsAvc } from '@shared-logic/business-particularities';

export function isValidQuota(quota: Quotas): boolean {
    return isValidDate(quota.date) != '--' && quota.trucks > 0 && quota.trucks % 1 == 0;
}

export const updateOrderStatusObject = (order: Order, orderStatus: number, message: string): Order => {
    let orderCopy: Order = JSON.parse(JSON.stringify(order));

    if (orderCopy.statusOrder != orderStatus &&
        orderCopy.statusOrder != orderStatusConst.RETURNED &&
        orderCopy.statusOrder != orderStatusConst.CONTRACT
    ) {
        orderCopy.message = message;
        orderCopy.statusOrder = orderStatus;
    }
    return orderCopy;
}

/**
 * Given a price and a discount, it is applied if the discount is greater than 0
 * The priceDiscount is only applied to the price if and only if it is greater than 0
 * @param price business price
 * @param priceDiscount discount to apply
 * @returns 
 */
export function getNewPriceWithDiscounts(price: number, priceDiscount: number) {
    return priceDiscount > 0 ? price - (price * priceDiscount / 100) : price;
}

/**
 * Given an exchange struct, it returns true if it has an incomplete field.
 * Only 3 are analyzed because the others may be incomplete.
 * @param exchange 
 * @returns boolean
 */
export function isExchangeIncomplete(exchange: Exchange | null) {
    return !exchange ||
        exchange.placeOfOrigin.afip <= 0 ||
        exchange.howSell <= 0 ||
        exchange.buyer.codeS <= 0;
}

/**
 * Given an array of orders of exchanges. Performs a summation of all the values stored in each 'tons' field
 * @param orders Array of orders of exchanges
 * @returns The sum of all the values stored in each 'tons' field
 */
export function getSumOfTonsExchange(orders: OrderExchange[]) {
    let sum = 0;
    for (const order of orders) {
        sum += order.exchange.tons ?? 0;
    }
    return parseFloat(sum.toFixed(3)); // Retorna la sumatoria con un máximo de 3 decimales
}

export function getHowSellIBM(howSell: number): string {
    const howSellStruct: any = {
        1: 'A',
        2: 'P',
        3: 'C',
        4: 'O'
    };
    return howSellStruct[howSell] ?? '';
}

/*transforms the SellerData code into a Buyer code with mapped buyerCode field*/
export function getBuyerExchange(order: Order): number {
    return order.sellerData.buyerCode ?? 0;
}

export function isSyngentaAgroAvc(order: OrderExchange): boolean {
    return getBuyerExchange(order) == SYNGENTA_CODE_BUYER && getIsAvc(order.price.businessParticularities) == 'A';
}

/**
 * Dada una orden de canje, modificamos la fecha de inicio del periodo de entrega y el inicio del periodo de fijación si son anteriores a hoy.
 * @param { OrderExchange } order - Orden a modificar
 * @returns { OrderExchange }
 */
export function getOrderWithNewDates(order: OrderExchange): OrderExchange {
    let newOrder: OrderExchange = JSON.parse(JSON.stringify(order));

    // Si la fecha de inicio del periodo de entrega es anterior a hoy, la actualizamos
    if (!isSameDate(stringToDate(newOrder.price.deliveryPeriod.startDate), new Date()) &&
        isEarlierDate(newOrder.price.deliveryPeriod.startDate, getDate(), '-')) {
        newOrder.price.deliveryPeriod.startDate = getDate();
    }

    // Si la orden tiene indicadores y la fecha de inicio de fijación es anterior a hoy, la actualizamos
    const hasIndicators = (newOrder?.price?.indicators?.length ?? 0) > 0;
    if (hasIndicators &&
        !isSameDate(stringToDate(newOrder.price.indicators[0].fixationPeriod.startDate), new Date()) &&
        isEarlierDate(newOrder.price.indicators[0].fixationPeriod.startDate, getDate(), '-')) {
        newOrder.price.indicators[0].fixationPeriod.startDate = getDate();
    }

    return newOrder;
}
